/**
 * Entity resolution — plan §8.1.
 *
 * ActOS takes people and companies from four places: the HubSpot mirror, a
 * people-data vendor's prospecting (Amplemarket or ContactOut), bulk imports,
 * and hands typing them in. The same person arrives from more than one, and
 * the system has to know it is the same person.
 *
 * **The rule that governs everything here: a contact is matched by EMAIL, and
 * by nothing else.** Not by name, not by name plus company, not by fuzzy
 * anything. Two people called Dana Levi at Northwind is not a hypothetical, and
 * the cost of getting it wrong is emailing one person content written about
 * another — under a sending identity whose reputation pays for it.
 *
 * Companies are different and can be matched on domain, because a domain IS the
 * identity of a company for our purposes: it decides gate 6's cap and the
 * suppression check. Name matching is offered only as a SUGGESTION for a human,
 * never applied automatically — "Acme Inc" and "Acme Industries" are one
 * company about half the time.
 */

export type EntitySource = 'crm' | 'amplemarket' | 'contactout' | 'manual' | 'import';

/**
 * Normalise an address for comparison.
 *
 * Deliberately conservative. Gmail's dots-and-plus-tags rules are real, but
 * applying them universally would merge `sales+eu@company.com` and
 * `sales+us@company.com` at a company that uses tags to route to different
 * teams — different mailboxes, different humans.
 *
 * So: lowercase and trim, and nothing else. Two addresses that differ by a dot
 * are treated as two addresses, which is wrong in the direction that sends one
 * extra email rather than the direction that sends the wrong person's email.
 */
export function normaliseEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function emailDomain(email: string): string {
  const at = email.lastIndexOf('@');
  return at === -1 ? '' : email.slice(at + 1).trim().toLowerCase();
}

/**
 * Public suffixes we treat as "not a company domain".
 *
 * A prospect writing from gmail.com does not make Gmail an account, and letting
 * it would put every unrelated free-mail contact under one enormous company —
 * which gate 6 would then cap collectively, silently throttling real outreach.
 */
const FREE_MAIL = new Set([
  'gmail.com',
  'googlemail.com',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'yahoo.com',
  'yahoo.co.uk',
  'icloud.com',
  'me.com',
  'aol.com',
  'proton.me',
  'protonmail.com',
  'gmx.com',
  'mail.com',
  'yandex.com',
  'qq.com',
  '163.com',
]);

export function isFreeMailDomain(domain: string): boolean {
  return FREE_MAIL.has(domain.toLowerCase());
}

/** The same set, as a list — for a SQL `not in`. */
export const FREE_MAIL_DOMAINS: readonly string[] = [...FREE_MAIL];

/**
 * Normalise a company domain for matching.
 *
 * Strips protocol, path, `www.` and a trailing dot. Does NOT strip other
 * subdomains: `uk.company.com` may genuinely be a different entity, and
 * collapsing it is a decision for a human.
 */
export function normaliseDomain(value: string | null | undefined): string | null {
  if (!value) return null;

  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/^[a-z]+:\/\//, '')
    .replace(/\/.*$/, '')
    .replace(/^www\./, '')
    .replace(/\.$/, '');

  // A bare label is not a domain; storing one would match far too much.
  return /^[a-z0-9-]+(\.[a-z0-9-]+)+$/.test(cleaned) ? cleaned : null;
}

/**
 * Normalise a company name for the SUGGESTION path only.
 *
 * Drops the legal-form suffixes and punctuation that make "Acme, Inc." and
 * "Acme Inc" look different to a string comparison while being the same to a
 * human.
 */
export function normaliseCompanyName(value: string | null | undefined): string | null {
  if (!value) return null;

  const cleaned = value
    .trim()
    .toLowerCase()
    .replace(/[.,]/g, ' ')
    .replace(
      /\b(inc|incorporated|llc|l\.l\.c|ltd|limited|plc|gmbh|ag|bv|nv|sa|sas|srl|spa|oy|ab|as|pty|corp|corporation|co|company|holdings|group|international|technologies|technology|labs|software|solutions)\b/g,
      ' ',
    )
    .replace(/\s+/g, ' ')
    .trim();

  return cleaned.length >= 2 ? cleaned : null;
}

export type MatchStrength = 'exact' | 'probable' | 'none';

export interface ContactMatch {
  strength: MatchStrength;
  /** Why, in words, for the audit trail and the review UI. */
  reason: string;
}

export interface ContactCandidate {
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  companyDomain?: string | null;
}

export interface ExistingContact {
  id: number;
  primaryEmail: string;
  alternateEmails?: string[];
}

/**
 * Find the existing contact this candidate IS, if any.
 *
 * Only ever returns `exact`. There is no probable tier for people, by design:
 * a probable person-match that a human forgets to review is an email to the
 * wrong human, and no amount of downstream care recovers from that.
 */
export function matchContact(
  candidate: ContactCandidate,
  existing: readonly ExistingContact[],
): { match: ExistingContact | null; result: ContactMatch } {
  const email = normaliseEmail(candidate.email);

  for (const row of existing) {
    if (normaliseEmail(row.primaryEmail) === email) {
      return {
        match: row,
        result: { strength: 'exact', reason: `Same email address (${email}).` },
      };
    }

    // A contact who replied from a second address has it recorded; matching on
    // it is still an exact email match, just not the primary one.
    if (row.alternateEmails?.some((alt) => normaliseEmail(alt) === email)) {
      return {
        match: row,
        result: {
          strength: 'exact',
          reason: `${email} is a known alternate address for this contact.`,
        },
      };
    }
  }

  return {
    match: null,
    result: { strength: 'none', reason: 'No contact with this email address exists.' },
  };
}

export interface AccountCandidate {
  name?: string | null;
  domain?: string | null;
}

export interface ExistingAccount {
  id: number;
  name: string;
  domain: string | null;
  normalisedName: string | null;
}

export interface AccountMatch {
  strength: MatchStrength;
  reason: string;
  /**
   * True when the caller must NOT apply the match automatically. Set for every
   * name-based match: two companies sharing a normalised name are one company
   * about half the time, and the other half is a merge that cannot be undone
   * without unpicking every contact attached to it.
   */
  needsReview: boolean;
}

export function matchAccount(
  candidate: AccountCandidate,
  existing: readonly ExistingAccount[],
): { match: ExistingAccount | null; result: AccountMatch } {
  const domain = normaliseDomain(candidate.domain);

  if (domain && !isFreeMailDomain(domain)) {
    for (const row of existing) {
      if (row.domain && normaliseDomain(row.domain) === domain) {
        return {
          match: row,
          result: {
            strength: 'exact',
            reason: `Same company domain (${domain}).`,
            needsReview: false,
          },
        };
      }
    }
  }

  const name = normaliseCompanyName(candidate.name);
  if (name) {
    for (const row of existing) {
      if (row.normalisedName === name) {
        return {
          match: row,
          result: {
            strength: 'probable',
            reason: `"${candidate.name}" normalises to the same name as "${row.name}", but they have different domains. A human should confirm they are one company.`,
            needsReview: true,
          },
        };
      }
    }
  }

  return {
    match: null,
    result: { strength: 'none', reason: 'No existing account matches.', needsReview: false },
  };
}

/**
 * Which source wins for a given field — §8.1's "CRM is truth for what it owns".
 *
 * A contact known to both HubSpot and a data vendor takes its name and title
 * from HubSpot, because that is the record a human curates and the one the
 * sales team sees. The vendor still contributes anything HubSpot lacks, which
 * is usually the reason it was consulted. The two vendors rank equally: a
 * contact revealed by one and later re-found by the other is the same
 * third-party data, and which arrived first should not decide the title.
 */
export const SOURCE_PRECEDENCE: Record<EntitySource, number> = {
  crm: 3,
  manual: 2,
  amplemarket: 1,
  contactout: 1,
  import: 1,
};

export function preferredSource(sources: readonly EntitySource[]): EntitySource | null {
  if (sources.length === 0) return null;
  return [...sources].sort((a, b) => SOURCE_PRECEDENCE[b] - SOURCE_PRECEDENCE[a])[0]!;
}

/**
 * Merge field values by source precedence, skipping blanks.
 *
 * A higher-precedence source with an EMPTY title must not erase a lower one's:
 * "HubSpot owns this record" does not mean "HubSpot's silence is authoritative".
 */
export function mergeField<T>(
  contributions: readonly { source: EntitySource; value: T | null | undefined }[],
): T | null {
  const present = contributions.filter(
    (c) => c.value !== null && c.value !== undefined && String(c.value).trim() !== '',
  );

  if (present.length === 0) return null;

  return present.sort(
    (a, b) => SOURCE_PRECEDENCE[b.source] - SOURCE_PRECEDENCE[a.source],
  )[0]!.value as T;
}
